import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import PaycardUpgradeService from "../services/PaycardUpgradeService";
import { CreditCard, Search, Clear, Lock } from "@mui/icons-material";
import Labels from "../labels/Labels";

export default function UpgradeCardDialog({
  open,
  onClose,
  cardToUpgrade,
  employerIdentifier,
}) {
  const [selectedCard, setSelectedCard] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // Fetch available cards
  const {
    data: availableCards,
    isLoading: isLoadingCards,
    isError: isErrorCards,
    error: errorCards,
  } = useQuery({
    queryKey: ["availableCards", employerIdentifier],
    queryFn: async () => {
      const response = await HttpService.getAxiosClient().get(
        `${window.API_URL}/paycards/available?employerIdentifier=${employerIdentifier}&productType=${cardToUpgrade?.cardType}`
      );
      return response.data;
    },
    enabled: open, // Only fetch when dialog is open
  });

  // Filter cards based on search text
  const filteredCards = useMemo(() => {
    if (!availableCards || !filterText.trim()) {
      return availableCards;
    }

    const searchText = filterText.toLowerCase().trim();
    return availableCards.filter(
      (card) =>
        card.cardNumber?.toLowerCase().includes(searchText) ||
        card.cardProxy?.toLowerCase().includes(searchText) ||
        card.status?.toLowerCase().includes(searchText) ||
        card.locationDescription?.toLowerCase().includes(searchText)
    );
  }, [availableCards, filterText]);

  // Clear selected card if it's not in filtered results
  useEffect(() => {
    if (
      selectedCard &&
      filteredCards &&
      !filteredCards.find((card) => card.id === selectedCard.id)
    ) {
      setSelectedCard(null);
    }
  }, [filteredCards, selectedCard]);

  // Mutation for card upgrade
  const upgradeMutation = useMutation({
    mutationFn: async (upgradeData) => {
      // Use the PaycardUpgradeService which implements the business logic
      // of finding customer by email and creating bulk action
      return await PaycardUpgradeService.upgradeCardDirect(upgradeData);
    },
    onSuccess: (data) => {
      setFeedback({
        open: true,
        type: "success",
        message:
          data.message ||
          Labels.getLabel(
            "upgradeCardDialog.successMessage",
            "Card upgraded successfully!"
          ),
      });
      setTimeout(() => {
        onClose(true); // Pass true to indicate success
      }, 2000);
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        Labels.getLabel(
          "upgradeCardDialog.errorMessage",
          "Failed to upgrade card"
        );
      setFeedback({
        open: true,
        type: "error",
        message: message,
      });
    },
  });

  const handleCardSelect = (card) => {
    if (card.inventoryLocked) return;
    setSelectedCard(card);
  };

  const handleUpgrade = () => {
    if (!selectedCard || !cardToUpgrade) return;
    console.log("Sending upgrade request:", {
      currentCardId: cardToUpgrade.id,
      newCardId: selectedCard.id,
      employeeNo: cardToUpgrade.employeeNo,
      type: "RH", // Adding type field for HR recarding
    });

    upgradeMutation.mutate({
      currentCardId: cardToUpgrade.id,
      newCardId: selectedCard.id,
      employeeNo: cardToUpgrade.employeeNo,
      type: "RH", // Adding type field for HR recarding
    });
  };

  const handleClose = () => {
    setSelectedCard(null);
    setFilterText("");
    setFeedback({ open: false, type: "error", message: "" });
    onClose(false);
  };

  const handleFeedbackClose = () => {
    setFeedback({ open: false, type: "error", message: "" });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: 400 },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CreditCard />
            <Typography variant="h6">
              {Labels.getLabel("upgradeCardDialog.title", "Upgrade Card")} -{" "}
              {cardToUpgrade?.cardNumber
                ? `****${cardToUpgrade.cardNumber}`
                : ""}
            </Typography>
          </Box>
          {cardToUpgrade?.extraInfo01 && (
            <Typography variant="body2" color="text.secondary">
              {Labels.getLabel("upgradeCardDialog.member", "Member")}:{" "}
              {cardToUpgrade.extraInfo01} ({cardToUpgrade.employeeNo})
            </Typography>
          )}
        </DialogTitle>

        <DialogContent>
          {isLoadingCards ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                {Labels.getLabel(
                  "upgradeCardDialog.loadingCards",
                  "Loading available cards..."
                )}
              </Typography>
            </Box>
          ) : isErrorCards ? (
            <Alert severity="error">
              {Labels.getLabel(
                "upgradeCardDialog.failedToLoad",
                "Failed to load available cards"
              )}
              : {errorCards?.message}
            </Alert>
          ) : availableCards?.length === 0 ? (
            <Alert severity="info">
              {Labels.getLabel(
                "upgradeCardDialog.noAvailableCards",
                "No available cards found for upgrade."
              )}
            </Alert>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                {Labels.getLabel(
                  "upgradeCardDialog.selectCard",
                  "Select a card to upgrade to"
                )}
                :
                {filterText && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    ({filteredCards?.length || 0}{" "}
                    {Labels.getLabel("upgradeCardDialog.of", "of")}{" "}
                    {availableCards?.length || 0}{" "}
                    {Labels.getLabel("upgradeCardDialog.cards", "cards")})
                  </Typography>
                )}
              </Typography>

              {/* Filter Input */}
              <TextField
                fullWidth
                variant="outlined"
                placeholder={Labels.getLabel(
                  "upgradeCardDialog.searchPlaceholder",
                  "Search cards by number, proxy, status, or location..."
                )}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: filterText && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setFilterText("")}
                        edge="end"
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {/* Cards List */}
              {filteredCards?.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {Labels.getLabel(
                    "upgradeCardDialog.noMatchingCards",
                    "No cards match your search criteria."
                  )}
                </Alert>
              ) : (
                <List sx={{ maxHeight: 300, overflow: "auto" }}>
                  {filteredCards?.map((card) => (
                    <ListItem key={card.id} disablePadding>
                      <ListItemButton
                        selected={selectedCard?.id === card.id}
                        onClick={() => handleCardSelect(card)}
                        disabled={card.inventoryLocked}
                        sx={{
                          border: selectedCard?.id === card.id ? 2 : 1,
                          borderColor:
                            selectedCard?.id === card.id
                              ? "primary.main"
                              : "divider",
                          borderRadius: 1,
                          mb: 1,
                          "&:hover": {
                            borderColor: "primary.main",
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {Labels.getLabel(
                                  "upgradeCardDialog.cardNumber",
                                  "Card"
                                )}{" "}
                                ****{card.cardNumber}
                              </Typography>
                              <Chip
                                label={card.status}
                                size="small"
                                color={
                                  card.status === "AVAILABLE"
                                    ? "success"
                                    : "default"
                                }
                              />
                              {card.inventoryLocked && (
                                <Chip
                                  icon={<Lock />}
                                  label={Labels.getLabel(
                                    "upgradeCardDialog.cardNotEnabled",
                                    "Not enabled for assignment"
                                  )}
                                  size="small"
                                  color="warning"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {Labels.getLabel(
                                  "upgradeCardDialog.cardEmbossing",
                                  "Card Embossing"
                                )}
                                : {card.embossing}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {Labels.getLabel(
                                  "upgradeCardDialog.cardProxy",
                                  "Card Proxy"
                                )}
                                : {card.cardProxy}
                              </Typography>
                              {card.locationDescription && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {Labels.getLabel(
                                    "upgradeCardDialog.location",
                                    "Location"
                                  )}
                                  : {card.locationDescription}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}

          {feedback.open && (
            <Alert
              severity={feedback.type}
              onClose={handleFeedbackClose}
              sx={{ mt: 2 }}
            >
              {feedback.message}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={upgradeMutation.isPending}>
            {Labels.getLabel("upgradeCardDialog.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleUpgrade}
            variant="contained"
            disabled={
              !selectedCard ||
              upgradeMutation.isPending ||
              isLoadingCards ||
              isErrorCards
            }
          >
            {upgradeMutation.isPending ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {Labels.getLabel("upgradeCardDialog.upgrading", "Upgrading...")}
              </>
            ) : (
              Labels.getLabel("upgradeCardDialog.upgradeCard", "Upgrade Card")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
